import { FC } from 'react';
import { Card } from '@app/shared/components/card/Card';
import { FormSelect } from '@app/shared/components/form-controls/form-select/FormSelect';
import { percentageValidator, getRequiredValidator } from '@core/validations/hook-forms/validators';
import FormDatePicker from '@app/shared/components/form-controls/form-date-picker/FormDatePicker';
import FormField from '@app/shared/components/form-controls/form-field/FormField';
import {
  amountBasicFieldFormattingProps,
  percentageFieldFormattingProps,
} from '@app/shared/components/form-controls/form-field/form-field-patterns';
import { getYesNoOptions } from '@app/shared/constants/dropdown-values/yes-no-options';
import { getInvestmentMetricOptions } from '@app/shared/constants/dropdown-values/benchmark/investment-metric-options';
import { getInvestmentHorizonOptions } from '@app/shared/constants/dropdown-values/benchmark/investment-horizon-options';
import { useFormContext } from 'react-hook-form';
import { BenchmarkingDto } from '@app/shared/models/contracts/project-dto';
import { InvestmentMetric, ProjectStatus } from '@app/shared/models/contracts/enums/shared-enums';
import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { currencyDenominationPair, enumKeyByValue } from '@app/shared/helpers';
import { FormValue } from '@app/shared/components/form-controls/form-value/FormValue';
import {
  amountRules,
  calculatedMultiplierRules,
} from '@app/shared/components/cell-value/CellValueConfigurations';
import { useLocale } from '@app/core/hooks/useLocale';

const DealMetrics: FC = (): JSX.Element => {
  const { watch } = useFormContext<BenchmarkingDto>();
  const investmentMetric = watch('dealMetrics.investmentMetric');
  const {
    storeBenchmarkingData,
    status,
    details: { currency, denomination },
    sourcesAndUses: { dayOneTransactionPurchasePrice },
  } = useAppSelector((state) => state.project.projectDraft);
  const { benchmarkingInputs } = useAppSelector((state) => state.benchmarking);
  const { l } = useLocale();
  const shouldFieldsBeRequired =
    status === enumKeyByValue(ProjectStatus, ProjectStatus.Closed) && storeBenchmarkingData;

  return (
    <Card>
      <h3>{l('_DealMetrics')}</h3>
      <div className="form-row form-row--three-column">
        <div className="form-row__column">
          <FormValue
            label={l('_EntryEnterpriseValue', {
              currencyAndDenomination: currencyDenominationPair(currency, denomination),
              interpolation: { escapeValue: false },
            })}
            value={dayOneTransactionPurchasePrice}
            {...amountRules}
            isGapless
          />
        </div>
        <div className="form-row__column">
          <FormValue
            label={l('_LtmMetricMultiple', {
              metric:
                investmentMetric !== null
                  ? `$t(${InvestmentMetric[investmentMetric]})`
                  : '{metric}',
              interpolation: { skipOnVariables: false },
            })}
            value={benchmarkingInputs?.ltmMetricMultiple}
            {...calculatedMultiplierRules}
            isGapless
          />
        </div>
        <div className="form-row__column">
          <FormDatePicker
            name="dealMetrics.debtMaturityDate"
            label={l('_DebtMaturityDate')}
            isGapless
            rules={{ ...(shouldFieldsBeRequired && getRequiredValidator()) }}
          />
        </div>
      </div>
      <div className="form-row form-row--three-column">
        <div className="form-row__column">
          <FormSelect
            name="dealMetrics.investmentMetric"
            label={l('_InvestmentMetric')}
            options={getInvestmentMetricOptions()}
            rules={{ ...(shouldFieldsBeRequired && getRequiredValidator()) }}
            isGapless
          />
        </div>
        <div className="form-row__column">
          <FormValue
            label={l('_TotalEquityFunding', {
              currencyAndDenomination: currencyDenominationPair(currency, denomination),
              interpolation: { escapeValue: false },
            })}
            value={benchmarkingInputs?.totalInvestment ?? 0}
            {...amountRules}
            isGapless
          />
        </div>
        <div className="form-row__column">
          <FormSelect
            name="dealMetrics.investmentHorizonYears"
            label={l('_InvestmentHorizonYears')}
            options={getInvestmentHorizonOptions()}
            rules={{ ...(shouldFieldsBeRequired && getRequiredValidator()) }}
            isGapless
          />
        </div>
      </div>
      <div className="form-row form-row--three-column">
        <div className="form-row__column">
          <FormField
            name="dealMetrics.entryOfTarget"
            label={l('_EntryMetricOfTarget', {
              metric:
                investmentMetric !== null
                  ? `$t(${InvestmentMetric[investmentMetric]})`
                  : '{metric}',
              currencyAndDenomination: currencyDenominationPair(currency, denomination),
              interpolation: { escapeValue: false, skipOnVariables: false },
            })}
            placeholder="0"
            {...amountBasicFieldFormattingProps}
            rules={{ ...(shouldFieldsBeRequired && getRequiredValidator()) }}
            isGapless
          />
        </div>
        <div className="form-row__column">
          <FormField
            name="dealMetrics.thirdPartyLeveragePercentOfEV"
            label={l('_ThirdPartyLeveragePercentageOfEv')}
            rules={{
              ...(shouldFieldsBeRequired && getRequiredValidator()),
              ...percentageValidator,
            }}
            {...percentageFieldFormattingProps}
            isGapless
          />
        </div>
        <div className="form-row__column">
          <FormSelect
            name="dealMetrics.investorMonitoringExitFees"
            label={l('_InvestorMonitoringExitFees')}
            options={getYesNoOptions()}
            isGapless
          />
        </div>
      </div>
    </Card>
  );
};

export default DealMetrics;
